import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Save, Eye, Code, Mail, Variable, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import TiptapEditor, { insertVariable } from "@/components/TiptapEditor";
import { useAuth } from "@/context/AuthContext";

const TRIGGER_BADGE = {
  "auth:request_otp": { label: "Login OTP", tint: "bg-amber-50 text-amber-700 border-amber-200" },
  "ticket:created": { label: "Ticket created", tint: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  "ticket:status_change": { label: "Status change", tint: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  "ticket:reminder": { label: "Reminder", tint: "bg-amber-50 text-amber-700 border-amber-200" },
  "cron:sla_breach": { label: "SLA breach", tint: "bg-rose-50 text-rose-700 border-rose-200" },
  "ticket:dept_link": { label: "Dept resolve link", tint: "bg-sky-50 text-sky-700 border-sky-200" },
  "ticket:cancelled": { label: "Cancellation", tint: "bg-rose-50 text-rose-700 border-rose-200" },
  "manual": { label: "Manual send", tint: "bg-violet-50 text-violet-700 border-violet-200" },
};

const BLANK_NEW = {
  key: "",
  name: "",
  subject: "",
  body_html: "<p>Hi {{ ticket.customer_name }},</p><p>Your ticket <strong>{{ ticket.code }}</strong>…</p>",
  trigger: "manual",
  description: "",
};

export default function EmailTemplateManager() {
  const { hasPerm } = useAuth();
  const canCreate = hasPerm("email_templates.create");
  const canEdit = hasPerm("email_templates.edit");
  const canDelete = hasPerm("email_templates.delete");
  const [templates, setTemplates] = useState([]);
  const [variables, setVariables] = useState([]);
  const [activeKey, setActiveKey] = useState("");
  const [draft, setDraft] = useState({ subject: "", body_html: "", active: true });
  const [preview, setPreview] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showHtml, setShowHtml] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTpl, setNewTpl] = useState(BLANK_NEW);

  const active = useMemo(() => templates.find((t) => t.key === activeKey), [templates, activeKey]);

  const load = async () => {
    setLoading(true);
    try {
      const [tpls, vars] = await Promise.all([
        api.get("/email-templates"),
        api.get("/email-templates/variables"),
      ]);
      setTemplates(tpls.data);
      setVariables(vars.data);
      if (!activeKey && tpls.data[0]) setActiveKey(tpls.data[0].key);
    } catch {
      toast.error("Unable to load templates — need edit_templates permission");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!active) return;
    setDraft({ subject: active.subject || "", body_html: active.body_html || "", active: active.active !== false });
    setPreview(null);
    setPreviewMode(false);
  }, [active]);

  const save = async () => {
    if (!activeKey) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/email-templates/${activeKey}`, {
        subject: draft.subject, body_html: draft.body_html, active: draft.active,
      });
      setTemplates((arr) => arr.map((t) => (t.key === activeKey ? data : t)));
      toast.success("Template saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    if (!activeKey) return;
    try {
      const { data } = await api.post(`/email-templates/${activeKey}/preview`, {
        subject: draft.subject, body_html: draft.body_html,
      });
      setPreview(data);
      setPreviewMode(true);
    } catch {
      toast.error("Preview render failed");
    }
  };

  const createTemplate = async () => {
    if (!newTpl.key.trim() || !newTpl.name.trim() || !newTpl.subject.trim()) {
      return toast.error("Key, Name and Subject are required.");
    }
    setCreating(true);
    try {
      const { data } = await api.post("/email-templates", {
        key: newTpl.key.trim(),
        name: newTpl.name.trim(),
        subject: newTpl.subject,
        body_html: newTpl.body_html,
        trigger: newTpl.trigger || "manual",
        description: newTpl.description || "",
        active: true,
      });
      toast.success(`Template "${data.name}" created`);
      setTemplates((arr) => [...arr, data]);
      setActiveKey(data.key);
      setCreateOpen(false);
      setNewTpl(BLANK_NEW);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  const deleteTemplate = async () => {
    if (!active) return;
    if (active.is_seed) return toast.error("Seeded templates cannot be deleted — toggle Active off instead.");
    if (!window.confirm(`Delete custom template "${active.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/email-templates/${active.key}`);
      toast.success("Template deleted");
      const remaining = templates.filter((t) => t.key !== active.key);
      setTemplates(remaining);
      setActiveKey(remaining[0]?.key || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) {
    return <div className="text-center py-16 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5">
      {/* Sidebar: template list */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">
              <Mail className="w-3.5 h-3.5 inline mr-1.5" /> Templates ({templates.length})
            </div>
            <Button size="sm" variant="ghost" className="h-7 text-[11px] text-indigo-600 hover:text-indigo-700"
                    onClick={() => { setNewTpl(BLANK_NEW); setCreateOpen(true); }}
                    disabled={!canCreate}
                    data-testid="tpl-new-btn">
              <Plus className="w-3 h-3 mr-0.5" /> New
            </Button>
          </div>
          <ul className="divide-y divide-slate-100" data-testid="template-list">
            {templates.map((t) => {
              const tag = TRIGGER_BADGE[t.trigger];
              return (
                <li key={t.key}>
                  <button
                    onClick={() => setActiveKey(t.key)}
                    data-testid={`tpl-list-${t.key}`}
                    className={`w-full text-left px-4 py-3 text-sm hover:bg-slate-50 ${
                      t.key === activeKey ? "bg-indigo-50 border-l-2 border-indigo-500" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium text-slate-800 truncate">{t.name}</div>
                      {!t.is_seed && <Badge variant="outline" className="text-[9px] bg-violet-50 text-violet-700 border-violet-200 shrink-0">Custom</Badge>}
                    </div>
                    <div className="text-[10px] font-mono text-slate-400 mt-0.5">{t.key}</div>
                    {tag && <Badge variant="outline" className={`text-[9px] mt-1.5 ${tag.tint}`}>{tag.label}</Badge>}
                    {t.active === false && <Badge variant="outline" className="ml-1 text-[9px] bg-slate-100 text-slate-500">off</Badge>}
                  </button>
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>

      {/* Editor + preview */}
      {active && (
        <Card className="border-slate-200" data-testid="template-editor-panel">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="font-heading text-base font-semibold text-slate-900">{active.name}</h3>
                <div className="text-[11px] text-slate-500 font-mono mt-0.5">key: {active.key} · trigger: {active.trigger}</div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={draft.active} onCheckedChange={(v) => setDraft((d) => ({ ...d, active: v }))} data-testid="tpl-active-switch" />
                <span className="text-xs text-slate-600">Active</span>
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-700">Subject</Label>
              <Input
                value={draft.subject}
                onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))}
                className="mt-1.5 font-medium"
                data-testid="tpl-subject-input"
              />
            </div>

            {/* Variable picker */}
            <div className="border border-indigo-100 bg-indigo-50/40 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider text-indigo-700 font-bold mb-2 flex items-center gap-1.5">
                <Variable className="w-3.5 h-3.5" /> Insert variable
              </div>
              <div className="flex flex-wrap gap-1.5">
                {variables.map((v) => (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => insertVariable(v.key)}
                    title={v.label}
                    data-testid={`var-chip-${v.key}`}
                    className="px-2 py-1 text-[10px] font-mono bg-white border border-indigo-200 text-indigo-700 rounded hover:bg-indigo-100 hover:border-indigo-300"
                  >
                    {`{{${v.key}}}`}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs font-semibold text-slate-700">Body</Label>
                <Button
                  type="button" size="sm" variant="ghost"
                  onClick={() => setShowHtml((v) => !v)}
                  className="text-[11px] h-7"
                  data-testid="tpl-toggle-html"
                >
                  <Code className="w-3 h-3 mr-1" />
                  {showHtml ? "WYSIWYG" : "Raw HTML"}
                </Button>
              </div>
              {showHtml ? (
                <textarea
                  value={draft.body_html}
                  onChange={(e) => setDraft((d) => ({ ...d, body_html: e.target.value }))}
                  rows={16}
                  className="w-full font-mono text-xs p-3 border border-slate-200 rounded-lg resize-y bg-slate-900 text-slate-100"
                  data-testid="tpl-html-textarea"
                />
              ) : (
                <TiptapEditor
                  value={draft.body_html}
                  onChange={(html) => setDraft((d) => ({ ...d, body_html: html }))}
                  minHeight={320}
                />
              )}
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              {!active.is_seed && canDelete && (
                <Button variant="outline" onClick={deleteTemplate} className="text-rose-600 hover:text-rose-700" data-testid="tpl-delete-btn">
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                </Button>
              )}
              <Button variant="outline" onClick={runPreview} data-testid="tpl-preview-btn">
                <Eye className="w-3.5 h-3.5 mr-1.5" /> Preview
              </Button>
              {canEdit && (
                <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="tpl-save-btn">
                  <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving..." : "Save"}
                </Button>
              )}
            </div>

            {/* Preview pane */}
            {previewMode && preview && (
              <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="tpl-preview-pane">
                <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 text-xs text-slate-600">
                  <span className="text-slate-400 mr-2">Subject:</span>
                  <span className="font-medium text-slate-800">{preview.subject}</span>
                </div>
                <iframe
                  title="email-preview"
                  srcDoc={preview.html}
                  className="w-full bg-white"
                  style={{ height: 520, border: "none" }}
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create-template dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="tpl-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Plus className="w-4 h-4 text-indigo-600" /> New custom template
            </DialogTitle>
            <DialogDescription className="text-xs">
              Add a brand-new email template you can fire from any ticket via the "Send email" action.
              Use a unique lowercase key (e.g. <code className="text-indigo-700">followup_reminder</code>).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Key <span className="text-rose-500">*</span></Label>
                <Input value={newTpl.key}
                       onChange={(e) => setNewTpl((t) => ({ ...t, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") }))}
                       placeholder="followup_reminder" className="font-mono text-xs"
                       data-testid="tpl-new-key" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Display name <span className="text-rose-500">*</span></Label>
                <Input value={newTpl.name}
                       onChange={(e) => setNewTpl((t) => ({ ...t, name: e.target.value }))}
                       placeholder="Customer follow-up"
                       data-testid="tpl-new-name" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Subject <span className="text-rose-500">*</span></Label>
              <Input value={newTpl.subject}
                     onChange={(e) => setNewTpl((t) => ({ ...t, subject: e.target.value }))}
                     placeholder="Following up on ticket {{ ticket.code }}"
                     data-testid="tpl-new-subject" />
              <p className="text-[10px] text-slate-500">Use <code className="text-indigo-700">{`{{ ticket.code }}`}</code> etc. — full body can be edited after creation.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input value={newTpl.description}
                     onChange={(e) => setNewTpl((t) => ({ ...t, description: e.target.value }))}
                     placeholder="Short note about when to send this"
                     data-testid="tpl-new-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createTemplate} disabled={creating}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="tpl-new-create-btn">
              {creating ? "Creating…" : "Create template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
